import { Formik, Form, Field, FormikSelect } from "collections/Form";
import {
  Animation,
  PolyIcon,
  ProgressBar,
  SubmitButton,
  ToolTip
} from "elements";
import { FormikErrors, FormikHelpers, FormikValues } from "formik/dist/types";
import React, { useContext, useEffect } from "react";
import Dropzone from "react-dropzone";
import { documentPreventDragDropEvents } from "utilities/fileUtils";
import { useTranslation } from "utilities/i18n";
import { Schema } from "yup";
import fileUploadAnimation from "assets/animations/File-Upload.json";
import { useSelector } from "react-redux";
import { State } from "store";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { FileInfo, ImportState } from "store/import";
import { ImportType } from "./Import";

const UPDATE_EXISTING_DEVICES_DEFAULT = true;
const OVERWRITE_DEVICE_RECORDS_DEFAULT = true;

export const FileUpload: React.FC<{
  dragText: string;
  dropText: string;
  fileTypesAccepted: string;
  multiple?: boolean;
  onDone?: () => void;
  onSubmit: (
    files: File[],
    updateExistingDevices: boolean,
    overwriteDeviceRecords: boolean
  ) => void;
  validationSchema: Schema<FormikValues>;
  disabled?: boolean;
  disabledReason?: string;
  status: {
    inProgress: boolean;
    progress: number;
    finished: boolean;
    success: boolean;
    successMessage?: string;
    errorContent?: React.ReactNode;
    files: FileInfo[];
  };
  importType: ImportType;
}> = ({
  dragText,
  dropText,
  fileTypesAccepted,
  multiple = false,
  onSubmit,
  onDone,
  validationSchema,
  disabled = false,
  disabledReason,
  status,
  importType
}) => {
  const { t } = useTranslation();

  useEffect(documentPreventDragDropEvents, []);

  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const {
    updateExistingDevices = UPDATE_EXISTING_DEVICES_DEFAULT,
    overwriteDeviceRecords = OVERWRITE_DEVICE_RECORDS_DEFAULT
  } = useSelector((state: State) => {
    const importState = state.import[tenantId];
    return importState && importState.type === importType
      ? importState
      : ({} as ImportState);
  });

  const handleUpload = (
    values: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    const filesToSubmit = multiple ? values.files : values.files.slice(0, 1);
    onSubmit(
      filesToSubmit,
      values.updateExistingDevices,
      values.overwriteDeviceRecords === "true"
    );
    actions.setSubmitting(false);
  };

  const getFileError = (errors: FormikErrors<FormikValues>, index: number) => {
    return (errors.files as FormikErrors<FormikValues>)?.[index] || undefined;
  };

  const {
    inProgress,
    progress,
    finished,
    success,
    successMessage,
    errorContent,
    files
  } = status;

  // This is just used to initialize form and show file name and type if import is active
  // It will never be used for anything else, hence empty file is sufficient
  const fileList = files.map(
    file => new File([], file.name, { type: file.type })
  );

  return (
    <Formik
      initialValues={{
        files: fileList,
        updateExistingDevices,
        overwriteDeviceRecords: overwriteDeviceRecords.toString()
      }}
      enableReinitialize
      name="FileUpload"
      onSubmit={handleUpload}
      validationSchema={validationSchema}
    >
      {({ dirty, errors, isSubmitting, isValid, setFieldValue, values }) => {
        const importFinished = () => {
          setFieldValue("files", []);
          setFieldValue(
            "updateExistingDevices",
            UPDATE_EXISTING_DEVICES_DEFAULT
          );
          setFieldValue(
            "overwriteDeviceRecords",
            OVERWRITE_DEVICE_RECORDS_DEFAULT.toString()
          );
          onDone && onDone();
        };

        const removeFile = (index: number) => {
          setFieldValue("files", [
            ...values.files.slice(0, index),
            ...values.files.slice(index + 1)
          ]);
        };

        const importInProgress = inProgress || finished;
        if (!importInProgress && disabled && values.files.length) {
          setFieldValue("files", []);
        }

        if (!values.updateExistingDevices && values.overwriteDeviceRecords) {
          setFieldValue("overwriteDeviceRecords", false);
        }

        return (
          <Form>
            {!!values.files.length && (
              <div>
                <div className="file-info-container">
                  {/* Validation response area */}
                  {values.files.map((file: File, index: number) => {
                    const fileError = getFileError(errors, index);
                    return (
                      <div key={`${file.name}-${index}`}>
                        <div className="filename-container mb-1">
                          <span
                            className={`${fileError ? "invalid" : ""}`}
                            data-testid="uploaded-filename"
                          >
                            {file.name}
                          </span>
                          {!fileError && (
                            <PolyIcon
                              icon="check_circle_outline"
                              className="checkmark"
                            />
                          )}
                          {!inProgress && (
                            <button
                              type="button"
                              className="btn btn-link remove-file ml-5"
                              data-testid="remove-file"
                              onClick={
                                multiple
                                  ? () => removeFile(index)
                                  : () => importFinished()
                              }
                            >
                              {t("FILE_REMOVE")}
                            </button>
                          )}
                        </div>
                        {fileError && (
                          <div className="error-text">
                            {t("FILE_VALIDATION_ERROR_DESC", {
                              errors: fileError
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/*Import options*/}
                  {importType === "DEVICE" ? (
                    <div className="mt-2 mb-2">
                      <label
                        htmlFor="updateExistingDevices"
                        className="font-regular grid fitted"
                      >
                        <span className="col-3">
                          {t("UPDATE_EXISTING_DEVICES")}:
                        </span>
                        <span className="col-4 pl-0">
                          <Field
                            name="updateExistingDevices"
                            type="checkbox"
                            data-testid="update-existing-devices"
                            disabled={importInProgress}
                          />
                        </span>
                      </label>
                      <label
                        htmlFor="overwriteDeviceRecords"
                        className="mt-1 font-regular grid fitted"
                      >
                        <span className="col-3">
                          {t("EXISTING_DEVICE_POLICY_BEHAVIOR")}:
                        </span>
                        <FormikSelect
                          name="overwriteDeviceRecords"
                          options={[
                            {
                              value: "true",
                              text: t("REPLACE_DEVICE_POLICY")
                            },
                            {
                              value: "false",
                              text: t("MERGE_DEVICE_POLICY")
                            }
                          ]}
                          disabled={
                            !values.updateExistingDevices || importInProgress
                          }
                          className="d-inline-block col-4 pl-0"
                        />
                      </label>
                    </div>
                  ) : null}
                  {/* Import response area */}
                  {importInProgress && (
                    <div className="grid fitted mb-1">
                      <ProgressBar
                        progress={progress * 100}
                        className={importType === "DEVICE" ? "col-7" : "col-6"}
                      />
                    </div>
                  )}
                  {finished && (
                    <div>
                      {success ? (
                        <>
                          <div className="success-text mb-5">
                            {successMessage || t("FILE_IMPORT_SUCCESS")}
                          </div>
                          {!multiple && (
                            <button
                              type="button"
                              className="btn btn-link"
                              onClick={importFinished}
                            >
                              {t("FILE_UPLOAD_ANOTHER")}
                            </button>
                          )}
                        </>
                      ) : (
                        <div>
                          {errorContent || (
                            <span className="error-text">
                              {t("FILE_IMPORT_ERROR")}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Button area */}
                <div className="btn-container mt-1">
                  <button
                    className={`btn ${success && "btn-primary"}`}
                    disabled={isSubmitting || inProgress}
                    onClick={importFinished}
                    type="button"
                  >
                    {success ? t("DONE") : t("CANCEL")}
                  </button>
                  {!success && (
                    <SubmitButton
                      isSubmitting={inProgress}
                      disabled={isSubmitting || !dirty || !isValid || finished}
                      labelKey="IMPORT"
                      submittingLabelKey="IMPORTING"
                    />
                  )}
                </div>
              </div>
            )}
            {/* Drag and Drop area */}
            {(multiple || !values.files.length) && (
              <Dropzone
                noDragEventsBubbling={true}
                onDrop={newFiles =>
                  setFieldValue("files", [...values.files, ...newFiles])
                }
                disabled={disabled}
              >
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <ToolTip
                    iconTitle="help"
                    size="lg"
                    iconReplacement={
                      <div
                        className={`dropzone mt-5 p-5 ${
                          isDragActive ? "dropzone-active" : ""
                        } ${disabled ? "disabled" : ""}`}
                        {...getRootProps()}
                        // Adding these additional style properties is necessary due to a conflict between dropzone and the current version of @types/react
                        style={{
                          ...getRootProps().style,
                          appearance: "none",
                          columnFill: "balance",
                          MozColumnFill: "balance",
                          WebkitColumnFill: "balance"
                        }}
                      >
                        <input
                          {...getInputProps()}
                          data-testid="file-input"
                          accept={fileTypesAccepted}
                          disabled={disabled}
                          style={{
                            ...getInputProps().style,
                            appearance: "none",
                            columnFill: "balance",
                            MozColumnFill: "balance",
                            WebkitColumnFill: "balance"
                          }}
                        />
                        {isDragActive ? (
                          <div className="drag-area">
                            <Animation
                              animation={{
                                light: fileUploadAnimation,
                                dark: fileUploadAnimation
                              }}
                              className="upload-icon-animated"
                              loop={false}
                            />
                            <span className="drag-text">{dragText}</span>
                          </div>
                        ) : (
                          <div className="drag-area">
                            <PolyIcon
                              icon="cloud_upload"
                              className="upload-icon outline"
                            />
                            <p>{dropText}</p>
                          </div>
                        )}
                      </div>
                    }
                    className="block"
                    disablePopup={!disabled}
                  >
                    {disabledReason}
                  </ToolTip>
                )}
              </Dropzone>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
